import React from 'react';

import { Drawer } from '../../design-system/components/desktop/complex';
import { Rating, Skeleton } from '../../design-system/components/desktop';
import { ConsoleEmpty, ConsoleError } from '../../design-system/patterns/console';

import './seller-reviews.css';

/**
 * A seller's reviews, in a panel over the store.
 *
 * WHY A PANEL AND NOT A SECTION. The store page is a shelf; its job is the
 * cards. A reviews block under the grid would push the cards up the page and
 * compete with the thing a buyer came for, and a whole `/seller/:id/reviews`
 * route is a navigation and a way back for what is usually a short list. The
 * trust signal already lives in the band, so the way IN belongs there too: the
 * stars become the control and this opens behind them.
 *
 * `Drawer` rather than `Modal` or `Sheet` directly, because it already resolves
 * the track the way this page needs -- a side panel above 1024, the mobile
 * `Sheet` organism below it -- and brings the focus trap, the scroll lock,
 * Escape and scrim dismissal with it.
 *
 * FETCHED ON OPEN, NEVER ON MOUNT. The store page's load is the grid; reviews
 * are a thing a buyer asks for. `Drawer` renders nothing while closed, so
 * mounting this component costs nothing until someone opens it.
 */
function ReviewRow({ review }) {
  return (
    <li className="dsa-review">
      <div className="dsa-review__head">
        <Rating value={review.rating} size="sm" showValue={false} />
        <span className="dsa-review__who">{review.reviewer}</span>
      </div>
      {/* A rating left without words still counts toward the stars above, so it
          is listed rather than hidden -- it just has nothing to quote. */}
      {review.review ? <p className="dsa-review__body">{review.review}</p> : null}
    </li>
  );
}

export function SellerReviews({
  open,
  onClose,
  storeName,
  reviews = [],
  total = 0,
  loading = false,
  error,
  onRetry,
}) {
  let body;

  if (loading) {
    body = (
      <div aria-hidden="true" className="dsa-reviews__loading">
        {[0, 1, 2].map((n) => (
          <div key={n} className="dsa-review">
            <Skeleton shape="text" width="40%" />
            <Skeleton shape="text" width="90%" />
          </div>
        ))}
      </div>
    );
  } else if (error) {
    body = <ConsoleError error={error} onRetry={onRetry} title="Could not load reviews" />;
  } else if (reviews.length === 0) {
    /* Reachable even with a non-zero count: the band's number and this list
       come from two different requests, so a store can open the panel between
       them. Saying so beats an empty box. */
    body = (
      <ConsoleEmpty
        title="No reviews yet"
        text="Reviews appear here once buyers rate their orders with this seller."
      />
    );
  } else {
    body = (
      <ul className="dsa-reviews__list">
        {reviews.map((review) => (
          <ReviewRow key={review.id} review={review} />
        ))}
      </ul>
    );
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      kicker={storeName}
      title={total ? `Reviews (${Number(total).toLocaleString('en-ZA')})` : 'Reviews'}
      ariaLabel={`Reviews for ${storeName || 'this store'}`}
    >
      {body}
    </Drawer>
  );
}

export default SellerReviews;
